import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { generateDatabaseInsights, getDatabaseInsights, generateDatabaseQueries, generateChartsForDatabase, getDatabaseQueriesChartStatus } from '../services/api';
import { Sparkles, Lightbulb, Database as DatabaseIcon, Grid, Play, Database, BarChart3, Loader, ChevronDown } from 'lucide-react';
import { formatLocalTime } from '../utils/timezone';
import { PlainQueryDisplay } from '../components/ui/PlainQueryDisplay';
import { WriteGuard, ReadOnlyBanner } from '../components/auth/WriteGuard';

export const InsightQuery = () => {
    const { t } = useTranslation(['page-insight-query', 'common']);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { selectedDatabase, databases, setSelectedDatabase } = useDatabase();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [insights, setInsights] = useState([]);
    const [selectedInsight, setSelectedInsight] = useState(null);
    const [insightsPlanExists, setInsightsPlanExists] = useState(false);
    const [allQueriesGenerated, setAllQueriesGenerated] = useState(false);
    const [generatingQueries, setGeneratingQueries] = useState(false);
    const [generatingCharts, setGeneratingCharts] = useState(false);
    const [generatedChartQueryIds, setGeneratedChartQueryIds] = useState([]);
    const [allChartsGenerated, setAllChartsGenerated] = useState(false);

    const dbId = searchParams.get('db') || selectedDatabase?.id;

    // Filter databases to only show those with schema analysis completed
    const analyzedDatabases = databases.filter(db => 
        db.status && db.status.toLowerCase() === 'analyzed'
    );

    const handleDatabaseChange = (e) => {
        const dbId = e.target.value;
        const db = databases.find(db => db.id === parseInt(dbId));
        if (db) {
            setSelectedDatabase(db);
            // No toast notification for database switch
        }
    };

    useEffect(() => {
        if (dbId) {
            fetchInsights();
            fetchChartStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbId]);

    const fetchChartStatus = async () => {
        if (!dbId) return;
        try {
            const res = await getDatabaseQueriesChartStatus(dbId);
            const data = res.data?.data;

            if (data && data.queries) {
                // Track which queries have charts
                const queryIdsWithCharts = data.queries
                    .filter(q => q.has_chart)
                    .map(q => q.query_id);
                setGeneratedChartQueryIds(queryIdsWithCharts);
                // Set if ALL charts are generated
                setAllChartsGenerated(data.queries_with_chart === data.total_queries && data.total_queries > 0);
            }
        } catch (error) {
            console.error('Failed to fetch chart status:', error);
        }
    };

    const fetchInsights = async () => {
        if (!dbId) return;
        setLoading(true);
        try {
            const res = await getDatabaseInsights(dbId);
            const insightData = res.data?.data?.insights || [];

            setInsights(insightData);

            // Check if insights plan exists
            setInsightsPlanExists(insightData && insightData.length > 0);

            // Check if all queries have been generated (if all insights have a generated_query property)
            if (insightData && insightData.length > 0) {
                const allHaveQueries = insightData.every(insight => insight.generated_query);
                setAllQueriesGenerated(allHaveQueries);
            } else {
                setAllQueriesGenerated(false);
            }
        } catch (error) {
            console.error('Failed to fetch insights:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!dbId) return;
        setGenerating(true);
        try {
            await generateDatabaseInsights(dbId);
            // No toast notification - silently proceed
            fetchInsights();
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-insight-query:failedToGenerateInsights'), 'error');
        } finally {
            setGenerating(false);
        }
    };

    const handleGenerateQueries = async () => {
        if (!dbId) return;
        setGeneratingQueries(true);
        try {
            await generateDatabaseQueries(dbId);
            // No toast notification - silently proceed
            fetchInsights();
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-insight-query:failedToGenerateQueries'), 'error');
        } finally {
            setGeneratingQueries(false);
        }
    };

    const handleGenerateCharts = async () => {
        if (!dbId) return;
        setGeneratingCharts(true);
        try {
            await generateChartsForDatabase(dbId);

            // Refresh chart status after generation
            await fetchChartStatus();

            // No toast notification - silently proceed
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-insight-query:failedToGenerateCharts'), 'error');
        } finally {
            setGeneratingCharts(false);
        }
    };

    return (
        <div className="page-content">
                <ReadOnlyBanner className="mb-4" />
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="page-title">{t('page-insight-query:title')}</h1>
                            <p className="page-subtitle">{t('page-insight-query:subtitle')}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="relative min-w-[200px]">
                                <select
                                    value={dbId || ''}
                                    onChange={handleDatabaseChange}
                                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white appearance-none"
                                >
                                    <option value="">{t('page-insight-query:selectDatabase')}</option>
                                    {analyzedDatabases.map((db) => (
                                        <option key={db.id} value={db.id}>{db.name}</option>
                                    ))}
                                </select>
                                <ChevronDown
                                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                    aria-hidden
                                />
                            </div>
                            <WriteGuard>
                            {!insightsPlanExists ? (
                                <button
                                    onClick={handleGenerate}
                                    disabled={generating || !dbId}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    {generating ? t('page-insight-query:generating') : t('page-insight-query:generateInsights')}
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={handleGenerateQueries}
                                        disabled={generatingQueries || !dbId || allQueriesGenerated}
                                        className={`px-4 py-2 ${
                                            allQueriesGenerated ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
                                        } text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                                    >
                                        <Play className="w-4 h-4" />
                                        {generatingQueries ? t('page-insight-query:generating') : allQueriesGenerated ? t('page-insight-query:queriesGenerated') : t('page-insight-query:generateQueries')}
                                    </button>
                                    <button
                                        onClick={handleGenerateCharts}
                                        disabled={generatingCharts || !dbId || allChartsGenerated}
                                        className={`px-4 py-2 ${
                                            allChartsGenerated ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'
                                        } text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                                    >
                                        <BarChart3 className="w-4 h-4" />
                                        {generatingCharts ? t('page-insight-query:generating') : allChartsGenerated ? t('page-insight-query:chartsGenerated') : t('page-insight-query:generateCharts')}
                                    </button>
                                </>
                            )}
                            </WriteGuard>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">{allQueriesGenerated ? t('page-insight-query:totalQueries') : t('page-insight-query:totalInsights')}</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {allQueriesGenerated 
                                            ? insights.reduce((sum, insight) => sum + (insight.generated_queries ? insight.generated_queries.length : 0), 0)
                                            : insights.length
                                        }
                                    </p>
                                </div>
                                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                    <Lightbulb className="w-5 h-5 text-primary-600" />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">{t('page-insight-query:valueTypes')}</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        {Object.keys(
                                            insights.reduce((acc, insight) => {
                                                const value_type = insight.value_type || 'general';
                                                acc[value_type] = true;
                                                return acc;
                                            }, {})
                                        ).length}
                                    </p>
                                </div>
                                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                    <Grid className="w-5 h-5 text-primary-600" />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">{t('page-insight-query:databaseLabel')}</p>
                                    <p className="text-lg font-semibold text-gray-900 truncate">{selectedDatabase?.name || t('page-insight-query:noDatabaseSelectedValue')}</p>
                                </div>
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                    <DatabaseIcon className="w-5 h-5 text-green-600" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : !dbId ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-insight-query:noDatabaseSelectedTitle')}</h3>
                        <p className="text-gray-600 mb-3">{t('page-insight-query:noDatabaseSelectedBody')}</p>
                        <p className="text-sm text-gray-500">
                            {t('page-insight-query:noDatabaseSelectedHint')}
                        </p>
                    </div>
                ) : insights.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <img
                            src="/no-insights.svg"
                            alt={t('page-insight-query:noInsightsTitle')}
                            className="w-64 h-64 mx-auto mb-6"
                        />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('page-insight-query:noInsightsTitle')}</h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                            {t('page-insight-query:noInsightsBody')}
                        </p>
                        <WriteGuard>
                        <button
                            onClick={handleGenerate}
                            disabled={generating || !dbId}
                            className={`px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium flex items-center space-x-2 transition-colors mx-auto ${
                                generating ? 'opacity-75 cursor-not-allowed' : ''
                            }`}
                        >
                            {generating ? (
                                <>
                                    <Loader className="w-5 h-5 animate-spin" />
                                    <span>{t('page-insight-query:generating')}</span>
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-5 h-5" />
                                    <span>{t('page-insight-query:generateInsights')}</span>
                                </>
                            )}
                        </button>
                        </WriteGuard>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Group by value_type first, then by category */}
                        {Object.entries(
                            insights.reduce((acc, insight) => {
                                const value_type = insight.value_type || 'general';
                                if (!acc[value_type]) acc[value_type] = {};
                                
                                const category = insight.category || 'general';
                                if (!acc[value_type][category]) acc[value_type][category] = [];
                                
                                acc[value_type][category].push(insight);
                                return acc;
                            }, {})
                        ).map(([value_type, categories]) => (
                            <div key={value_type} className="space-y-4">
                                <h2 className="text-xl font-bold text-gray-900 capitalize">{value_type} {t('page-insight-query:insightsSuffix')}</h2>

                                {Object.entries(categories).map(([category, categoryInsights]) => (
                                    <div key={category} className="ml-4 space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800 capitalize">{category} {t('page-insight-query:analyticsSuffix')}</h3>
                                        <p className="text-sm text-gray-600 ml-2">{t('page-insight-query:insightsAvailable', { count: categoryInsights.length })}</p>

                                        <div className="space-y-3 ml-2">
                                            {categoryInsights.map((insight) => (
                                                <div key={insight.id} className="bg-white rounded-lg border border-gray-200 p-4">
                                                    <div className="flex-1">
                                                        <h4 className="font-semibold text-gray-900 mb-1">{insight.name}</h4>
                                                        <p className="text-sm text-gray-600">{insight.description}</p>
                                                    </div>

                                                    <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
                                                        <div>
                                                            <span className="font-semibold text-gray-700">{t('page-insight-query:statusLabel')}</span>
                                                            <span className="ml-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                                                                {insight.status || t('common:unknown')}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">{t('page-insight-query:priorityLabel')}</span>
                                                            <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded">
                                                                {insight.priority_score || t('page-insight-query:notAvailable')}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {insight.value_description && (
                                                        <div className="mt-3 p-3 bg-primary-50 rounded-lg border border-primary-100">
                                                            <p className="text-sm text-primary-900">
                                                                <span className="font-semibold">{t('page-insight-query:businessValueLabel')}</span> {insight.value_description}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {insight.required_tables && insight.required_tables.length > 0 && (
                                                        <div className="mt-3 flex items-center gap-2">
                                                            <span className="text-xs text-gray-500">{t('page-insight-query:tablesLabel')}</span>
                                                            <div className="flex flex-wrap gap-1">
                                                                {insight.required_tables.map((table, idx) => (
                                                                    <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                                                        {table}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {insight.generated_queries && insight.generated_queries.length > 0 && (
                                                        <div className="mt-3">
                                                            <div className="bg-green-50 rounded-lg border border-green-200 overflow-hidden">
                                                                {/* Insight Header */}
                                                                <div className="bg-green-100 border-b border-green-200 px-4 py-3">
                                                                    <div className="flex items-start justify-between">
                                                                        <div className="flex-1">
                                                                            <p className="text-sm font-semibold text-green-900">
                                                                                {insight.name}
                                                                            </p>
                                                                            <p className="text-xs text-green-800 mt-1">
                                                                                {insight.description}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {/* Queries List */}
                                                                <div className="p-4 space-y-3">
                                                                    {insight.generated_queries.map((query, idx) => (
                                                                        <div key={query.id} className="border border-green-200 rounded-lg p-3 bg-white">
                                                                            <div className="flex items-start justify-between mb-2">
                                                                                <div className="flex-1">
                                                                                    <p className="font-medium text-gray-900 text-sm flex items-center gap-2">
                                                                                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs font-bold">
                                                                                            {idx + 1}
                                                                                        </span>
                                                                                        {query.name}
                                                                                    </p>
                                                                                    {query.description && (
                                                                                        <p className="text-xs text-gray-600 mt-1 ml-7">{query.description}</p>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex gap-2 ml-4">
                                                                                    {generatedChartQueryIds.includes(query.id) && (
                                                                                        <button
                                                                                            onClick={() => navigate(`/charts?query=${query.id}&db=${dbId}`)}
                                                                                            className="px-2 py-1 bg-primary-600 text-white rounded text-xs font-medium hover:bg-primary-700 transition-colors flex items-center gap-1"
                                                                                        >
                                                                                            <BarChart3 className="w-3 h-3" />
                                                                                            {t('common:view')}
                                                                                        </button>
                                                                                    )}
                                                                                    {!generatedChartQueryIds.includes(query.id) && (
                                                                                        <span className="px-2 py-1 bg-gray-200 text-gray-500 rounded text-xs flex items-center gap-1">
                                                                                            <BarChart3 className="w-3 h-3" />
                                                                                            {t('page-insight-query:generateChartsFirst')}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <PlainQueryDisplay sql={query.query} />
                                                                            <div className="flex items-center gap-4 text-xs mt-2 ml-7">
                                                                                <span className={`px-2 py-1 rounded ${
                                                                                    query.validation_status === 'valid' ? 'bg-green-100 text-green-700' :
                                                                                    query.validation_status === 'invalid' ? 'bg-red-100 text-red-700' :
                                                                                    'bg-gray-100 text-gray-700'
                                                                                }`}>
                                                                                    {query.validation_status || t('page-insight-query:pending')}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="mt-3 text-xs text-gray-500">
                                                        {t('page-insight-query:createdLabel')} {formatLocalTime(insight.created_at)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}


                {/* Insight Details Modal */}
                {selectedInsight && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-auto">
                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-4">{selectedInsight.name}</h3>
                                <p className="text-sm text-gray-600 mb-4">{selectedInsight.description}</p>

                                <div className="space-y-4">
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">{t('common:details')}</h4>
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <p><span className="font-medium">{t('page-insight-query:categoryLabel')}</span> {selectedInsight.category || t('page-insight-query:notAvailable')}</p>
                                                <p><span className="font-medium">{t('page-insight-query:statusLabel')}</span> {selectedInsight.status || t('page-insight-query:notAvailable')}</p>
                                                <p><span className="font-medium">{t('page-insight-query:priorityScoreLabel')}</span> {selectedInsight.priority_score || t('page-insight-query:notAvailable')}</p>
                                            </div>
                                            <div>
                                                <p><span className="font-medium">{t('page-insight-query:valueTypeLabel')}</span> {selectedInsight.value_type || t('page-insight-query:notAvailable')}</p>
                                                <p><span className="font-medium">{t('page-insight-query:sourceLabel')}</span> {selectedInsight.source || t('page-insight-query:notAvailable')}</p>
                                                <p><span className="font-medium">{t('page-insight-query:createdAtLabel')}</span> {formatLocalTime(selectedInsight.created_at)}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {selectedInsight.value_description && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-2">{t('page-insight-query:businessValueLabel2')}</h4>
                                            <p>{selectedInsight.value_description}</p>
                                        </div>
                                    )}

                                    {selectedInsight.required_tables && selectedInsight.required_tables.length > 0 && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-2">{t('page-insight-query:requiredTables')}</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedInsight.required_tables.map((table, idx) => (
                                                    <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                                                        {table}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedInsight.generated_queries && selectedInsight.generated_queries.length > 0 && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-2">
                                                {t('page-insight-query:generatedQueriesCount', { count: selectedInsight.generated_queries.length })}
                                            </h4>
                                            <div className="space-y-3">
                                                {selectedInsight.generated_queries.map((query, idx) => (
                                                    <div key={query.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                        <div className="flex items-start justify-between mb-2">
                                                            <div className="flex-1">
                                                                <p className="font-medium text-gray-900 text-sm flex items-center gap-2">
                                                                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-700 text-xs font-bold">
                                                                        {idx + 1}
                                                                    </span>
                                                                    {query.name}
                                                                </p>
                                                                {query.description && (
                                                                    <p className="text-xs text-gray-600 mt-1 ml-7">{query.description}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <PlainQueryDisplay sql={query.query} />
                                                        <div className="flex items-center gap-4 text-xs mt-2 ml-7">
                                                            <span className={`px-2 py-1 rounded ${
                                                                query.validation_status === 'valid' ? 'bg-green-100 text-green-700' :
                                                                query.validation_status === 'invalid' ? 'bg-red-100 text-red-700' :
                                                                'bg-gray-100 text-gray-700'
                                                            }`}>
                                                                {query.validation_status || t('page-insight-query:pending')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end gap-2">
                                    <button
                                        onClick={() => setSelectedInsight(null)}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                    >
                                        {t('common:close')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};
